import React from 'react';
import { Box, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import StopRounded from '@mui/icons-material/StopRounded';

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** Shown in place of Send while `busy`. */
  onStop?: () => void;
  /**
   * An answer is being written: Send becomes Stop, the box is frozen — a
   * follow-up typed now could not be sent — and its border pulses.
   */
  busy?: boolean;
  /** Nothing can be sent right now, e.g. an approval is waiting. */
  disabled?: boolean;
  /** Send is held back for a reason, e.g. files still processing; says why on hover. */
  sendBlockedReason?: string;
  placeholder?: string;
  /** Above the text, inside the box: context chips, attached files. */
  above?: React.ReactNode;
  /** Before the text (or, stacked, under it on the left): an attach button. */
  start?: React.ReactNode;
  /** A quiet line under the box, e.g. the keyboard shortcuts. */
  hint?: string;
  /**
   * `inline` — one line that grows as you type, buttons beside it: follow-ups
   * in a chat.
   * `stacked` — a text area with room for a longer question, the buttons in a
   * row beneath it: a first question, on a home page.
   */
  variant?: 'inline' | 'stacked';
  'aria-label'?: string;
  inputRef?: React.Ref<HTMLTextAreaElement>;
}

/**
 * Where a question is typed. Enter sends, Shift+Enter starts a new line.
 *
 * While an answer is being written the box is frozen and its border pulses:
 * a follow-up cannot be sent mid-answer, so it should not be typeable, and
 * the box itself says the chat is busy. Send becomes a red Stop.
 */
export function ChatComposer({
  value,
  onChange,
  onSend,
  onStop,
  busy = false,
  disabled = false,
  sendBlockedReason,
  placeholder,
  above,
  start,
  hint,
  variant = 'inline',
  'aria-label': ariaLabel = 'Message',
  inputRef,
}: ChatComposerProps) {
  const stacked = variant === 'stacked';
  const canSend = !busy && !disabled && !sendBlockedReason && value.trim().length > 0;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (canSend) onSend();
  };

  const controlSize = (t: { ds: { sizes: { control: { sm: number } } } }) => t.ds.sizes.control.sm;

  const input = (
    <InputBase
      multiline
      minRows={stacked ? 3 : 1}
      maxRows={stacked ? 12 : 6}
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled || busy}
      inputRef={inputRef}
      inputProps={{ 'aria-label': ariaLabel }}
      sx={(t) => ({
        ...(stacked ? t.typography.body1 : t.typography.body2),
        color: t.tokens.text.primary,
        py: 0.5,
        '& textarea::placeholder': { color: t.tokens.text.tertiary, opacity: 1 },
      })}
    />
  );

  const action = busy ? (
    <Tooltip title="Stop">
      <IconButton
        aria-label="Stop"
        onClick={onStop}
        sx={(t) => ({
          width: controlSize(t),
          height: controlSize(t),
          flexShrink: 0,
          borderRadius: `${t.ds.radius.sm}px`,
          // Red, because it interrupts: a black square reads as a control
          // rather than "stop what you are doing".
          bgcolor: t.tokens.semantic.danger,
          color: t.tokens.brand.onPrimary,
          '&:hover': { bgcolor: t.tokens.semantic.danger, opacity: 0.88 },
        })}
      >
        <StopRounded fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : (
    <Tooltip title={sendBlockedReason || 'Send (Enter)'}>
      <span style={{ display: 'inline-flex' }}>
        <IconButton
          aria-label="Send"
          onClick={onSend}
          disabled={!canSend}
          sx={(t) => ({
            width: controlSize(t),
            height: controlSize(t),
            flexShrink: 0,
            borderRadius: `${t.ds.radius.sm}px`,
            bgcolor: t.tokens.brand.primary,
            color: t.tokens.brand.onPrimary,
            '&:hover': { bgcolor: t.tokens.brand.primaryHover },
            '&.Mui-disabled': { bgcolor: t.tokens.surface.sunken, color: t.tokens.text.disabled },
          })}
        >
          <ArrowUpwardRounded fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box>
      <Box
        sx={(t) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: stacked ? 1 : 0.75,
          px: stacked ? 2 : 1.5,
          py: stacked ? 1.5 : 1,
          bgcolor: t.tokens.chat.inputBg,
          border: `1px solid ${t.tokens.chat.inputBorder}`,
          borderRadius: `${t.ds.radius.lg}px`,
          boxShadow: t.tokens.chat.inputShadow,
          transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
          '&:focus-within': { borderColor: t.tokens.chat.inputFocusBorder },
          // While an answer is being written the box breathes, so the chat
          // reads as busy without a second line repeating what the steps say.
          ...(busy && {
            borderColor: t.tokens.brand.primary,
            '@keyframes kit-composer-breathe': {
              '0%, 100%': { borderColor: t.tokens.brand.primary },
              '50%': { borderColor: t.tokens.chat.inputBorder },
            },
            '@media (prefers-reduced-motion: no-preference)': {
              animation: 'kit-composer-breathe 1.8s ease-in-out infinite',
            },
          }),
        })}
      >
        {above}
        {stacked ? (
          <>
            {input}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {start}
              <Box sx={{ flex: 1 }} />
              {action}
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75 }}>
            {start}
            {input}
            {action}
          </Box>
        )}
      </Box>
      {hint && (
        <Typography
          variant="caption"
          component="div"
          sx={(t) => ({ mt: 0.75, textAlign: 'center', color: t.tokens.text.tertiary })}
        >
          {hint}
        </Typography>
      )}
    </Box>
  );
}
